@for (entry of entries(); track entry.id) {
  <div class="je-summary">
    <div class="je-summary__header">
      <span class="pi pi-book je-summary__icon"></span>
      <span class="je-summary__number">{{ entry.number }}</span>
      @if (labelFor(entry); as caption) {
        <!-- Which step of the document booked this. Only rendered when the host supplies one, which it does
             when it posts more than one entry and the numbers cannot tell them apart. -->
        <span class="je-summary__caption">{{ caption }}</span>
      }
      <p-tag [severity]="statusSeverity(entry.status)" [value]="statusLabel(entry.status) | translateWF: statusLabel(entry.status)" />
      <span class="je-summary__date">{{ entry.postingDate | date: 'dd/MM/yyyy' }}</span>
    </div>

    <p-table [value]="entry.lineItems" dataKey="lineNumber" styleClass="p-datatable-sm je-summary__table">
      <ng-template pTemplate="header">
        <tr>
          <th>{{ 'JournalEntry_Account' | translateWF: 'Account' }}</th>
          <th class="je-summary__amount-col">{{ 'JournalEntry_Debit' | translateWF: 'Debit' }}</th>
          <th class="je-summary__amount-col">{{ 'JournalEntry_Credit' | translateWF: 'Credit' }}</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-line>
        <tr>
          <td>
            <div class="je-summary__account-name">{{ line.accountName }}</div>
            <div class="je-summary__account-number">{{ line.accountNumber }}</div>
          </td>
          <!-- A line is either a debit or a credit; the other side stays blank rather than showing 0.00,
               so the two columns read as one ledger rather than a grid half-full of zeroes.

               Mirrors journal-entry-details: the BOOKS' figure leads on every line, and what actually moved
               sits beneath it in small text with the rate that got there — quoted the way a person says a
               rate, units per dollar, not the reciprocal the GL multiplies by. A reader comparing two lines
               of one entry is comparing dollars to dollars, which is the only comparison that means
               anything on a converted transfer. A functional-only line (the realized exchange difference)
               has no transaction amount at all and renders its functional figure directly. -->
          <td class="je-summary__amount-col tabular-nums">
            @if (isFunctionalOnly(line)) {
              @if (line.functionalDebitAmount) {
                {{ line.functionalDebitAmount | number: '1.2-2' }} <span class="je-summary__account-number">{{ functionalCurrencyText(entry) }}</span>
              }
            } @else if (line.debitAmount) {
              @if (showsConversion(entry, line)) {
                {{ line.functionalDebitAmount | number: '1.2-2' }} <span class="je-summary__account-number">{{ functionalCurrencyText(entry) }}</span>
                <div class="je-summary__account-number">
                  {{ line.debitAmount | number: '1.2-2' }} {{ effectiveCurrencyText(entry, line) }} &#64; {{ lineRateText(entry, line) }}
                </div>
              } @else {
                {{ line.debitAmount | number: '1.2-2' }}
              }
            }
          </td>
          <td class="je-summary__amount-col tabular-nums">
            @if (isFunctionalOnly(line)) {
              @if (line.functionalCreditAmount) {
                {{ line.functionalCreditAmount | number: '1.2-2' }} <span class="je-summary__account-number">{{ functionalCurrencyText(entry) }}</span>
              }
            } @else if (line.creditAmount) {
              @if (showsConversion(entry, line)) {
                {{ line.functionalCreditAmount | number: '1.2-2' }} <span class="je-summary__account-number">{{ functionalCurrencyText(entry) }}</span>
                <div class="je-summary__account-number">
                  {{ line.creditAmount | number: '1.2-2' }} {{ effectiveCurrencyText(entry, line) }} &#64; {{ lineRateText(entry, line) }}
                </div>
              } @else {
                {{ line.creditAmount | number: '1.2-2' }}
              }
            }
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="3" class="text-center">
            {{ 'General_NoRecords' | translateWF: 'No records found' }}
          </td>
        </tr>
      </ng-template>
    </p-table>

    <div class="je-summary__footer">
      <span class="je-summary__balanced" [class.je-summary__balanced--off]="!isBalanced(entry)">
        @if (isBalanced(entry)) {
          <span class="pi pi-check-circle"></span>
          {{ 'JournalEntry_Balanced' | translateWF: 'Balanced' }}
        } @else {
          <span class="pi pi-exclamation-triangle"></span>
          {{ 'JournalEntry_Unbalanced' | translateWF: 'Unbalanced' }}
        }
        <!-- A mixed entry's transaction total is a sum across currencies (and the host's label names the
             DOCUMENT's currency besides) — it totals in the books' currency, the only one every line
             shares (#5144). -->
        @if (totalsInFunctional(entry)) {
          <span class="je-summary__total tabular-nums">· {{ entry.totalFunctionalDebits | number: '1.2-2' }} {{ functionalCurrencyText(entry) }}</span>
        } @else {
          <span class="je-summary__total tabular-nums">· {{ entry.totalDebits | number: '1.2-2' }} {{ currencyLabel }}</span>
        }
      </span>

      <!-- Opens the full posting (#5026) in a NEW TAB, so reading the entry never costs the document this
           summary sits on — which may be a draft someone is halfway through. The details page can only
           offer `Back to Journal Entries`, so navigating in place would strand whoever came from here;
           until an entry carries a typed pointer to its source (#5028 item 2) it cannot build the trail
           back on its own.

           A real anchor rather than window.open, for the reason the bank account statement's transaction
           links give: the href is genuine, so middle-click, ctrl-click, the context menu and hover-preview
           all behave. `pi-arrow-up-right` for "this opens away from here". -->
      @if (canOpen(entry)) {
        <a
          pButton
          [label]="'JournalEntry_ViewFullEntry' | translateWF: 'View full entry'"
          icon="pi pi-arrow-up-right"
          iconPos="right"
          [text]="true"
          [routerLink]="['/journal-entry-details']"
          [queryParams]="{ id: entry.id }"
          target="_blank"
          rel="noopener"
        ></a>
      } @else {
        <!-- No id, or a host that renders an entry the user should not open. Inert but still there: the
             row keeps its shape either way. -->
        <p-button [label]="'JournalEntry_ViewFullEntry' | translateWF: 'View full entry'" icon="pi pi-arrow-up-right" iconPos="right" [text]="true" [disabled]="true" />
      }
    </div>
  </div>
}
